import { Fragment, type ElementType, useEffect, useMemo, useState } from "react";
import {
  ArrowBack,
  DirectionsRun,
  LocalFireDepartment,
  Straighten,
  Favorite,
  AccessTime,
  Bloodtype,
  MonitorHeart,
  Bedtime,
  MonitorWeight,
  Send,
  Close,
  History,
} from "@mui/icons-material";
import {
  alpha,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import { toast } from "react-toastify";
import {
  clearGoogleHealthcareAccessToken,
  fetchGoogleHealthFitnessBundle,
  GoogleHealthScopeError,
  GOOGLE_HEALTH_SCOPE_RECONNECT_MESSAGE,
  type IGoogleHealthFitnessBundle,
} from "@/services/google/health";
import { RefreshTokenGoogle } from "@/services/google/RefreshTokenGoogle";
import { GoogleHealthcareAuthCard } from "@/pages/profile/components/GoogleHealthcareAuthCard";
import { useSessionContext } from "@/contexts/SessionContext";
import { PageHeader } from "@/components/common/PageHeader";
import { ScreenMobile } from "@/components/common/ScreenMobile";
import { FitnessDataHistoryPanel } from "./FitnessDataHistoryPanel";
import { FitnessPeriodHeader } from "./FitnessPeriodHeader";
import { FitnessDataSharePanel } from "./FitnessDataSharePanel";
import { FitnessDataShareConfirmDialog } from "./FitnessDataShareConfirmDialog";
import { FitnessGoogleAccountFooter } from "./FitnessGoogleAccountFooter";
import {
  aggregateGoogleHealthForPeriod,
  formatarDadosGoogleHealth,
  formatDistanceFromMeters,
  formatSleepDurationFromMinutes,
  formatWeightFromKg,
  hasGoogleHealthMetricsInPeriod,
} from "@/utils/wearableFitFormat";
import {
  WEARABLE_LOINC_LIST,
  hasWearableValueForKey,
  hasWearableValueForKeyInPeriod,
  type WearableShareKey,
} from "@/services/fhir/observation/wearableObservationBuilder";
import {
  type FitnessRangeDays,
  isValidFitnessRangeDays,
} from "@/pages/medicalcare/components/Wearable/WearableDateRangeChips";
import { resolveWearablePeriod } from "@/pages/medicalcare/components/Wearable/wearableMetricAggregation";

interface FitnessDataModalProps {
  open?: boolean;
  onClose?: () => void;
  refreshToken: string;
  /** Conteúdo inline no dashboard mobile (sem Dialog/ScreenMobile) */
  embedded?: boolean;
  /** Em embedded: busca dados só quando a seção está expandida */
  active?: boolean;
}

type MetricKey =
  | "steps"
  | "calories"
  | "distance"
  | "heartRate"
  | "activeMinutes"
  | "glucose"
  | "bloodPressure"
  | "weight"
  | "sleep";

const EMPTY_FITNESS_BUNDLE: IGoogleHealthFitnessBundle = {
  steps: [],
  distance: [],
  sleep: [],
  electrocardiogram: [],
  heartRate: [],
  weight: [],
  queriedAt: "",
};

const METRIC_CONFIG: Array<{
  key: MetricKey;
  label: string;
  icon: ElementType;
  color: string;
}> = [
    { key: "steps", label: "Passos", icon: DirectionsRun, color: "#1E88E5" },
    { key: "calories", label: "Gasto de energia", icon: LocalFireDepartment, color: "#F4511E" },
    { key: "distance", label: "Distância", icon: Straighten, color: "#7B1FA2" },
    { key: "heartRate", label: "Frequência cardíaca", icon: Favorite, color: "#E53935" },
    { key: "weight", label: "Peso", icon: MonitorWeight, color: "#455A64" },
    { key: "activeMinutes", label: "Minutos em movimento", icon: AccessTime, color: "#00897B" },
    { key: "glucose", label: "Glicose", icon: Bloodtype, color: "#3949AB" },
    { key: "bloodPressure", label: "Pressão arterial", icon: MonitorHeart, color: "#6D4C41" },
    { key: "sleep", label: "Sono", icon: Bedtime, color: "#5E35B1" },
  ];

export function FitnessDataModal({
  open = false,
  onClose,
  refreshToken,
  embedded = false,
  active = true,
}: FitnessDataModalProps) {
  const theme = useTheme();
  /** Em telas estreitas o Dialog ocupa a viewport inteira para evitar cortes e scroll horizontal. */
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  /** Modal aberto ou seção embutida expandida — controla busca automática e navegação de datas */
  const isActive = embedded ? active : open;
  const contentCompact = embedded || isXs;
  const { user } = useSessionContext();
  const isConnected = Boolean(user?.refreshTokenGoogleFit);
  const today = dayjs().startOf("day");
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [rangeDays, setRangeDays] = useState<FitnessRangeDays>(1);
  const [loading, setLoading] = useState(false);
  const [fitnessBundle, setFitnessBundle] = useState<IGoogleHealthFitnessBundle | null>(
    null
  );
  /** Troca o conteúdo do mesmo modal (dados Google Health, envio ao prontuário ou histórico FHIR). */
  const [modalView, setModalView] = useState<"data" | "share" | "history">(
    "data"
  );
  /** Carregamento do GetObservation no painel de histórico (navegação de data bloqueada enquanto busca). */
  const [historyLoading, setHistoryLoading] = useState(false);
  /** Métricas marcadas para envio ao prontuário. */
  const [shareSelectedKeys, setShareSelectedKeys] = useState<Record<string, boolean>>({});
  /** Confirmação de envio (mobile/embedded — não troca de tela). */
  const [shareConfirmOpen, setShareConfirmOpen] = useState(false);
  /** Histórico FHIR em tela cheia (embedded / mobile) — sem sub-navegação na seção inicial. */
  const [historyOpen, setHistoryOpen] = useState(false);
  const googleHealthcareClientId =
    import.meta.env.VITE_GOOGLE_HEALTHCARE_CLIENT_ID ||
    import.meta.env.VITE_GOOGLE_CLIENT_ID;
  const googleHealthcareClientSecret =
    import.meta.env.VITE_GOOGLE_HEALTHCARE_CLIENT_SECRET ||
    import.meta.env.VITE_GOOGLE_CLIENT_SECRET;

  const { periodStart, periodEnd } = useMemo(
    () => resolveWearablePeriod({ selectedDate, rangeDays, today }),
    [selectedDate, rangeDays, today]
  );

  const periodStartKey = periodStart.format("YYYY-MM-DD");
  const periodEndKey = periodEnd.format("YYYY-MM-DD");

  const fitData = useMemo(
    () => formatarDadosGoogleHealth(fitnessBundle, selectedDate),
    [fitnessBundle, selectedDate]
  );

  const periodAggregates = useMemo(
    () => aggregateGoogleHealthForPeriod(fitnessBundle, periodStart, periodEnd),
    [fitnessBundle, periodStart, periodEnd]
  );

  const displayFitData = rangeDays === 1 ? fitData : periodAggregates.total;

  const parsedMetrics = useMemo(() => {
    const source = displayFitData;
    return METRIC_CONFIG.map((metric) => {
      const isSleep = metric.key === "sleep";
      const isHeartRate = metric.key === "heartRate";
      const sleepMinutes =
        rangeDays === 1
          ? Number(source.sonoMinutos || 0)
          : Number(periodAggregates.average.sonoMinutos || 0);
      const sleepNights = periodAggregates.daysWithSleepData;

      return {
        ...metric,
        periodHint:
          rangeDays > 1
            ? isSleep
              ? sleepNights > 0
                ? `Média em ${sleepNights} ${sleepNights === 1 ? "noite" : "noites"}`
                : "Média por noite"
              : isHeartRate
                ? "Média no período"
                : "Total no período"
            : undefined,
        value:
          metric.key === "steps"
            ? String(Math.round(Number(source.passos || 0)))
            : metric.key === "calories"
              ? Number(source.calorias || 0).toFixed(1)
              : metric.key === "distance"
                ? formatDistanceFromMeters(Number(source.distanciaMetros || 0))
                : metric.key === "heartRate"
                  ? `${Number(source.bpmMedio || 0).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} bpm`
                  : metric.key === "weight"
                    ? formatWeightFromKg(Number(source.pesoKg || 0))
                    : metric.key === "activeMinutes"
                      ? String(Math.round(Number(source.minutosAtivos || 0)))
                      : metric.key === "glucose"
                        ? Number(source.glicose || 0).toFixed(1)
                        : metric.key === "bloodPressure"
                          ? source.pressao
                          : formatSleepDurationFromMinutes(sleepMinutes),
      };
    });
  }, [displayFitData, periodAggregates.average.sonoMinutos, periodAggregates.daysWithSleepData, rangeDays]);

  /** Ao mudar período/dados, pré-seleciona só métricas com valor para envio dinâmico. */
  useEffect(() => {
    if (!fitnessBundle) {
      setShareSelectedKeys({});
      return;
    }
    const initial: Record<string, boolean> = {};
    for (const def of WEARABLE_LOINC_LIST) {
      initial[def.key] =
        rangeDays === 1
          ? hasWearableValueForKey(fitData, def.key)
          : hasWearableValueForKeyInPeriod(
              fitnessBundle,
              periodStart,
              periodEnd,
              def.key
            );
    }
    setShareSelectedKeys(initial);
  }, [fitData, fitnessBundle, selectedDate, periodStart, periodEnd, rangeDays]);

  const selectableShareCount = useMemo(
    () =>
      WEARABLE_LOINC_LIST.filter((def) => {
        const hasValue =
          rangeDays === 1
            ? hasWearableValueForKey(fitData, def.key)
            : hasWearableValueForKeyInPeriod(
                fitnessBundle,
                periodStart,
                periodEnd,
                def.key
              );
        return hasValue && shareSelectedKeys[def.key];
      }).length,
    [fitData, shareSelectedKeys, fitnessBundle, periodStart, periodEnd, rangeDays]
  );

  /** Mobile/embedded: seleção nos cards + dialog de confirmação (sem tela de envio). */
  const useInlineShareSelection = contentCompact;

  const toggleShareKey = (key: WearableShareKey) => {
    const hasValue =
      rangeDays === 1
        ? hasWearableValueForKey(fitData, key)
        : hasWearableValueForKeyInPeriod(
            fitnessBundle,
            periodStart,
            periodEnd,
            key
          );
    if (!hasValue) return;
    setShareSelectedKeys((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    if (!isConnected) {
      setFitnessBundle(null);
    }
  }, [isConnected]);

  useEffect(() => {
    if (!isActive) {
      setModalView("data");
      setShareConfirmOpen(false);
      setHistoryOpen(false);
    }
  }, [isActive]);

  const historyDateBusy = historyLoading;
  const isHistoryContext = historyOpen || modalView === "history";
  const selectedDateKey = selectedDate.format("YYYY-MM-DD");
  const fetchPeriodKey = `${periodStartKey}_${periodEndKey}_${rangeDays}`;

  const openHistoryScreen = () => {
    if (embedded || isXs) {
      setHistoryOpen(true);
    } else {
      setModalView("history");
    }
  };

  const handleSearch = async (dateOverride?: Dayjs) => {
    if (!refreshToken) {
      toast.error("Conecte sua conta Google Health no perfil para consultar dados.");
      return;
    }
    const dateToSearch = (dateOverride || selectedDate).startOf("day");
    const { periodStart: searchStart, periodEnd: searchEnd } = resolveWearablePeriod({
      selectedDate: dateToSearch,
      rangeDays,
      today,
    });

    setLoading(true);
    try {
      let accessTokenGoogle = localStorage.getItem("googleHealthcareAccessToken") || "";

      if (!accessTokenGoogle) {
        const tokenData = await RefreshTokenGoogle(refreshToken, {
          clientId: googleHealthcareClientId,
          clientSecret: googleHealthcareClientSecret,
        });
        accessTokenGoogle = tokenData.access_token;
        localStorage.setItem("googleHealthcareAccessToken", accessTokenGoogle);
      }

      const { bundle, errors } = await fetchGoogleHealthFitnessBundle({
        selectedDate: dateToSearch,
        periodStart: searchStart,
        periodEnd: searchEnd,
        accessTokenGoogle,
        refreshToken,
        setAccessTokenGoogle: (value: string) =>
          localStorage.setItem("googleHealthcareAccessToken", value),
        googleClientId: googleHealthcareClientId,
        googleClientSecret: googleHealthcareClientSecret,
      });

      setFitnessBundle(bundle);

      if (errors.length > 0) {
        toast.warn(
          `Alguns dados não puderam ser carregados (${errors.map((e) => e.dataType).join(", ")}).`
        );
      }
    } catch (error) {
      console.error("Erro ao buscar dados fitness:", error);

      if (error instanceof GoogleHealthScopeError) {
        clearGoogleHealthcareAccessToken();
        setFitnessBundle(EMPTY_FITNESS_BUNDLE);
        toast.error(GOOGLE_HEALTH_SCOPE_RECONNECT_MESSAGE, { autoClose: 8000 });
        return;
      }

      setFitnessBundle(EMPTY_FITNESS_BUNDLE);
      toast.error("Não foi possível consultar os dados fitness.");
    } finally {
      setLoading(false);
    }
  };

  const handleChangeDay = (deltaDays: number) => {
    if (!isHistoryContext && modalView === "data" && loading) return;
    if (isHistoryContext && historyDateBusy) return;
    const candidateDate = selectedDate.add(deltaDays, "day");
    const newDate = candidateDate.isAfter(today, "day") ? today : candidateDate;
    setSelectedDate(newDate);
  };

  /** Data escolhida no calendário; limita até hoje. Busca dispara no useEffect abaixo. */
  const handlePickDate = (picked: Dayjs) => {
    if (!isHistoryContext && modalView === "data" && loading) return;
    if (isHistoryContext && historyDateBusy) return;
    const clamped = picked.isAfter(today, "day") ? today : picked.startOf("day");
    setSelectedDate(clamped);
  };

  const handleGoToToday = () => {
    if (modalView === "data" && loading) return;
    if (isHistoryContext && historyDateBusy) return;

    const todayStart = today.startOf("day");
    setSelectedDate(todayStart);
    if (open && isConnected && refreshToken && modalView === "data") {
      handleSearch(todayStart);
    }
  };

  useEffect(() => {
    if (!isActive) return;
    if (!isConnected) return;
    if (!refreshToken) return;
    if (isHistoryContext || modalView !== "data") return;
    handleSearch(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, isConnected, refreshToken, fetchPeriodKey, isHistoryContext, modalView]);

  /** Painel de envio ao prontuário (reutilizado no Dialog desktop e no ScreenMobile). */
  const sharePanel = (
    <FitnessDataSharePanel
      fitnessBundle={fitnessBundle}
      selectedDate={selectedDate}
      periodStart={periodStart}
      periodEnd={periodEnd}
      rangeDays={rangeDays}
      onBack={() => setModalView("data")}
      onCloseModal={embedded ? undefined : onClose}
      selectedKeys={shareSelectedKeys}
      onSelectedKeysChange={setShareSelectedKeys}
      compact={contentCompact}
      embedded={embedded}
    />
  );

  /** Observações FHIR enviadas (GetObservation) com agrupamento por horário de envio. */
  const historyPanel = (
    <FitnessDataHistoryPanel
      selectedDate={selectedDate}
      periodStart={periodStart}
      periodEnd={periodEnd}
      rangeDays={rangeDays}
      today={today}
      patientUsername={user?.username}
      onLoadingChange={setHistoryLoading}
      onPrevDay={() => handleChangeDay(-1)}
      onNextDay={() => handleChangeDay(1)}
      onPickDate={handlePickDate}
      onRangeDaysChange={(days) => {
        if (isValidFitnessRangeDays(days)) setRangeDays(days);
      }}
      compact={contentCompact && !historyOpen}
      hideDateRow
      fullScreen={historyOpen}
    />
  );

  /** Faixa de data igual à visão de dados Google Health (reutilizada no histórico em tela cheia). */
  const fitnessDateHeader = (dateLoading: boolean) => (
    <Paper
      elevation={0}
      sx={{
        p: contentCompact ? 1 : { xs: 1.5, sm: 2 },
        backgroundColor: "primary800.main",
        color: "white",
        borderRadius: 2,
        mb: contentCompact ? 1.25 : 2,
      }}
    >
      <FitnessPeriodHeader
        periodStart={periodStart}
        periodEnd={periodEnd}
        rangeDays={rangeDays}
        today={today}
        loading={dateLoading}
        onRangeDaysChange={(days) => {
          if (isValidFitnessRangeDays(days)) setRangeDays(days);
        }}
        onPrevDay={() => handleChangeDay(-1)}
        onNextDay={() => handleChangeDay(1)}
        onPickDate={handlePickDate}
        onGoToToday={handleGoToToday}
        compact={contentCompact}
      />
    </Paper>
  );

  const historyFullScreen = (
    <ScreenMobile
      open={historyOpen}
      onClose={() => setHistoryOpen(false)}
      title="Histórico de envio"
    >
      <Box sx={{ px: 1.5, py: 1, minWidth: 0 }}>
        {fitnessDateHeader(historyDateBusy)}
        {historyPanel}
      </Box>
    </ScreenMobile>
  );

  /** No mobile modal (não embutido): descrição + termos. */
  const mobileDataIntro = (
    <>
      <Typography variant="body2" color="text.secondary">
        Acompanhe e compartilhe dados do seu Google Health
      </Typography>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <Link
          href="https://fluxmed.com.br/politica-de-privacidade/"
          target="_blank"
          rel="noreferrer"
          underline="none"
        >
          <Chip
            label="Política de privacidade"
            size="small"
            clickable
            variant="outlined"
            sx={{
              fontWeight: 600,
              color: "primary800.main",
              borderColor: "primary300.main",
              bgcolor: (theme) => alpha(theme.palette.primary100.main, 0.7),
              "&:hover": {
                bgcolor: (theme) => alpha(theme.palette.primary100.main, 1),
                borderColor: "primary500.main",
              },
            }}
          />
        </Link>
      </Box>
    </>
  );

  const dataViewBody = (
    <Stack spacing={contentCompact ? 1.25 : 2} sx={{ pt: contentCompact ? 0.25 : 1 }}>
      {contentCompact && !embedded ? mobileDataIntro : null}
      {!embedded && !isConnected && (
        <GoogleHealthcareAuthCard
          compact
          showRevokeDialog={false}
          showDisconnect={false}
        />
      )}

      {isConnected && (
        <>
          <Paper
            elevation={0}
            sx={{
              p: contentCompact ? 1 : { xs: 1.5, sm: 2 },
              backgroundColor: "primary800.main",
              color: "white",
              borderRadius: 2,
            }}
          >
            <Stack spacing={contentCompact ? 1 : 1.5} alignItems="center" sx={{ width: "100%" }}>
              {/* Header em largura total (igual ao histórico) para a data não ser cortada. */}
              <Box sx={{ width: "100%", minWidth: 0 }}>
                <FitnessPeriodHeader
                  periodStart={periodStart}
                  periodEnd={periodEnd}
                  rangeDays={rangeDays}
                  today={today}
                  loading={loading}
                  onRangeDaysChange={(days) => {
                    if (isValidFitnessRangeDays(days)) setRangeDays(days);
                  }}
                  onPrevDay={() => handleChangeDay(-1)}
                  onNextDay={() => handleChangeDay(1)}
                  onPickDate={handlePickDate}
                  onGoToToday={handleGoToToday}
                  compact={contentCompact}
                />
              </Box>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={contentCompact ? 0.75 : 1}
                alignItems="center"
                justifyContent="center"
                flexWrap="wrap"
                sx={{ width: "100%", maxWidth: 720 }}
              >
                <Button
                  variant="outlined"
                  size={contentCompact ? "small" : "medium"}
                  startIcon={<History />}
                  onClick={openHistoryScreen}
                  disabled={loading || !user?.username}
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { sm: useInlineShareSelection ? undefined : 200 },
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: contentCompact ? "0.72rem" : undefined,
                    color: "white",
                    borderColor: (theme) => alpha(theme.palette.neutral0.main, 0.35),
                    bgcolor: (theme) => alpha(theme.palette.neutral0.main, 0.12),
                    "&:hover": {
                      borderColor: (theme) => alpha(theme.palette.neutral0.main, 0.5),
                      bgcolor: (theme) => alpha(theme.palette.neutral0.main, 0.2),
                    },
                    "&.Mui-disabled": {
                      color: (theme) => alpha(theme.palette.neutral0.main, 0.3),
                      borderColor: (theme) => alpha(theme.palette.neutral0.main, 0.15),
                      bgcolor: (theme) => alpha(theme.palette.neutral0.main, 0.06),
                    },
                  }}
                >
                  Histórico de envio
                </Button>
                {!useInlineShareSelection && (
                <Button
                  variant="contained"
                  size={contentCompact ? "small" : "medium"}
                  startIcon={<Send />}
                  onClick={() => setModalView("share")}
                  disabled={loading || !fitnessBundle || selectableShareCount === 0}
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { sm: 200 },
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: contentCompact ? "0.72rem" : undefined,
                    bgcolor: (theme) => alpha(theme.palette.neutral0.main, 0.22),
                    color: "white",
                    "&:hover": {
                      bgcolor: (theme) => alpha(theme.palette.neutral0.main, 0.32),
                    },
                  }}
                >
                  Enviar ao prontuário
                </Button>
                )}
              </Stack>
            </Stack>
          </Paper>

          <Stack spacing={contentCompact ? 1 : 1.5}>
            {loading ? (
              <Grid container spacing={contentCompact ? 1 : 1.5}>
                {Array.from({ length: contentCompact ? 4 : 8 }).map((_, index) => (
                  <Grid
                    item
                    xs={contentCompact ? 6 : 12}
                    sm={6}
                    md={4}
                    key={`skeleton-${index}`}
                  >
                    <Box
                      sx={{
                        p: contentCompact ? 0.55 : 1.5,
                        borderRadius: contentCompact ? 1.25 : 2,
                        border: "1px solid",
                        borderColor: contentCompact ? "divider" : "grey.200",
                        minHeight: contentCompact ? 52 : 120,
                        bgcolor: "background.paper",
                      }}
                    >
                      <Stack spacing={contentCompact ? 0.75 : 1.5}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Skeleton
                            variant="text"
                            width="55%"
                            height={contentCompact ? 14 : 22}
                          />
                          <Skeleton
                            variant="circular"
                            width={contentCompact ? 20 : 34}
                            height={contentCompact ? 20 : 34}
                          />
                        </Box>
                        <Skeleton
                          variant="text"
                          width="70%"
                          height={contentCompact ? 18 : 34}
                        />
                      </Stack>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            ) : fitnessBundle ? (
              <>
                {!hasGoogleHealthMetricsInPeriod(fitnessBundle, periodStart, periodEnd) && (
                  <Typography variant="body2" color="text.secondary">
                    {rangeDays === 1
                      ? "Nenhum dado retornado para este dia."
                      : "Nenhum dado retornado para este período."}
                  </Typography>
                )}
                <Grid container spacing={contentCompact ? 0.75 : 1.5}>
                  {parsedMetrics.map((metric) => {
                    const shareKey = metric.key as WearableShareKey;
                    const hasShareValue =
                      rangeDays === 1
                        ? hasWearableValueForKey(fitData, shareKey)
                        : hasWearableValueForKeyInPeriod(
                            fitnessBundle,
                            periodStart,
                            periodEnd,
                            shareKey
                          );
                    const isSelectedForShare = Boolean(shareSelectedKeys[shareKey]);

                    return (
                    <Grid
                      item
                      xs={contentCompact ? 6 : 12}
                      sm={6}
                      md={4}
                      key={metric.key}
                    >
                      <Box
                        role={
                          useInlineShareSelection && hasShareValue ? "button" : undefined
                        }
                        tabIndex={
                          useInlineShareSelection && hasShareValue ? 0 : undefined
                        }
                        aria-pressed={
                          useInlineShareSelection && hasShareValue
                            ? isSelectedForShare
                            : undefined
                        }
                        aria-disabled={
                          useInlineShareSelection && !hasShareValue ? true : undefined
                        }
                        onClick={() => {
                          if (useInlineShareSelection && hasShareValue) {
                            toggleShareKey(shareKey);
                          }
                        }}
                        onKeyDown={(event) => {
                          if (
                            !useInlineShareSelection ||
                            !hasShareValue ||
                            (event.key !== "Enter" && event.key !== " ")
                          ) {
                            return;
                          }
                          event.preventDefault();
                          toggleShareKey(shareKey);
                        }}
                        sx={{
                          p: contentCompact ? 0.55 : 1.5,
                          borderRadius: contentCompact ? 1.25 : 2,
                          border: "1px solid",
                          borderColor:
                            useInlineShareSelection && isSelectedForShare && hasShareValue
                              ? "primary.main"
                              : contentCompact
                                ? "divider"
                                : "grey.200",
                          borderWidth:
                            useInlineShareSelection && isSelectedForShare && hasShareValue ? 2 : 1,
                          minHeight: contentCompact ? 52 : 120,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                          gap: contentCompact ? 0.25 : 0,
                          bgcolor:
                            useInlineShareSelection && isSelectedForShare && hasShareValue
                              ? (t) => alpha(t.palette.primary.main, 0.05)
                              : "background.paper",
                          opacity: useInlineShareSelection && !hasShareValue ? 0.45 : 1,
                          cursor:
                            useInlineShareSelection && hasShareValue ? "pointer" : undefined,
                          userSelect:
                            useInlineShareSelection && hasShareValue ? "none" : undefined,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: useInlineShareSelection ? 0.25 : 0.5,
                            minWidth: 0,
                            justifyContent: "space-between",
                            width: "100%",
                          }}
                        >
                          {useInlineShareSelection && (
                            <Checkbox
                              size="small"
                              checked={hasShareValue && isSelectedForShare}
                              disabled={!hasShareValue}
                              tabIndex={-1}
                              sx={{
                                p: 0,
                                mt: -0.15,
                                flexShrink: 0,
                                pointerEvents: "none",
                              }}
                            />
                          )}
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontSize: contentCompact ? "0.58rem" : undefined,
                              lineHeight: 1.15,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              minWidth: 0,
                              flex: 1,
                            }}
                          >
                            {metric.label}
                          </Typography>
                          <Box
                            sx={{
                              width: contentCompact ? 20 : 34,
                              height: contentCompact ? 20 : 34,
                              borderRadius: "50%",
                              bgcolor: `${metric.color}20`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <metric.icon
                              sx={{
                                color: metric.color,
                                fontSize: contentCompact ? 12 : 18,
                              }}
                            />
                          </Box>
                        </Box>
                        <Typography
                          variant="h6"
                          color="text.primary"
                          sx={{
                            mt: contentCompact ? 0.15 : 0.5,
                            ml: useInlineShareSelection ? 2.75 : 0,
                            fontWeight: 700,
                            fontSize: contentCompact
                              ? "0.75rem"
                              : { xs: "1rem", sm: "1.25rem" },
                            wordBreak: "break-word",
                            lineHeight: 1.15,
                          }}
                        >
                          {metric.value}
                        </Typography>
                        {metric.periodHint ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              mt: 0.25,
                              ml: useInlineShareSelection ? 2.75 : 0,
                              display: "block",
                              fontSize: contentCompact ? "0.62rem" : "0.7rem",
                            }}
                          >
                            {metric.periodHint}
                          </Typography>
                        ) : null}
                      </Box>
                    </Grid>
                    );
                  })}
                </Grid>

                {useInlineShareSelection && (
                  <Button
                    fullWidth
                    variant="contained"
                    disableElevation
                    size="medium"
                    startIcon={<Send />}
                    onClick={() => setShareConfirmOpen(true)}
                    disabled={selectableShareCount === 0}
                    sx={{
                      mt: 1.25,
                      py: 1,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "0.8125rem",
                      boxShadow: (t) => `0 4px 14px ${alpha(t.palette.primary.main, 0.28)}`,
                    }}
                  >
                    Enviar ao prontuário
                    {selectableShareCount > 0 ? ` (${selectableShareCount})` : ""}
                  </Button>
                )}
              </>
            ) : null}
          </Stack>

          <FitnessGoogleAccountFooter isConnected compact={contentCompact} />
        </>
      )}

      {!isConnected && (
        embedded ? (
          <FitnessGoogleAccountFooter isConnected={false} compact={contentCompact} />
        ) : null
      )}
    </Stack>
  );

  /** Modo embutido no dashboard mobile — sem Dialog/ScreenMobile */
  const shareConfirmDialog = (
    <FitnessDataShareConfirmDialog
      open={shareConfirmOpen}
      onClose={() => setShareConfirmOpen(false)}
      fitnessBundle={fitnessBundle}
      selectedDate={selectedDate}
      periodStart={periodStart}
      periodEnd={periodEnd}
      rangeDays={rangeDays}
      selectedKeys={shareSelectedKeys}
      compact={contentCompact}
    />
  );

  if (embedded) {
    return (
      <Box sx={{ minWidth: 0 }}>
        {dataViewBody}
        {shareConfirmDialog}
        {historyFullScreen}
      </Box>
    );
  }

  return (
    <Fragment>
      {isXs ? (
        <>
          <ScreenMobile open={open} onClose={onClose!} title="Dados fitness">
            {dataViewBody}
          </ScreenMobile>
          {shareConfirmDialog}
          {historyFullScreen}
        </>
      ) : (
        <Dialog
          open={open}
          onClose={onClose}
          fullWidth
          maxWidth="md"
          scroll="paper"
        >
          <DialogTitle sx={{ p: { xs: 1, sm: 1.5 } }}>
            {modalView === "share" || modalView === "history" ? (
              <Box sx={{ position: "relative", pr: { xs: 5, sm: 5.5 } }}>
                <IconButton
                  onClick={onClose}
                  size="small"
                  aria-label="Fechar"
                  sx={{
                    position: "absolute",
                    top: { xs: 4, sm: 8 },
                    right: { xs: 0, sm: 4 },
                    zIndex: 1,
                    color: "neutral600.main",
                    bgcolor: (t) => alpha(t.palette.neutral300.main, 0.3),
                    "&:hover": {
                      bgcolor: (t) => alpha(t.palette.neutral300.main, 0.45),
                    },
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ minWidth: 0, pr: 0.5 }}
                >
                  <IconButton
                    onClick={() => setModalView("data")}
                    size="small"
                    aria-label="Voltar aos dados fitness"
                    sx={{
                      flexShrink: 0,
                      color: "primary800.main",
                      bgcolor: (theme) => alpha(theme.palette.neutral300.main, 0.35),
                      "&:hover": {
                        bgcolor: (theme) => alpha(theme.palette.neutral300.main, 0.5),
                      },
                    }}
                  >
                    <ArrowBack fontSize="small" />
                  </IconButton>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: "1rem", sm: "1.25rem" },
                      lineHeight: 1.25,
                      overflow: "hidden",
                      textOverflow: { xs: "unset", sm: "ellipsis" },
                      display: "-webkit-box",
                      WebkitLineClamp: { xs: 2, sm: 1 },
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {modalView === "share"
                      ? "Enviar dados ao prontuário"
                      : "Histórico de envio de dados"}
                  </Typography>
                </Stack>
              </Box>
            ) : (
              /**
               * Uma linha: título à esquerda; chip + fechar agrupados à direita (sem segunda faixa vazia).
               */
              <Stack
                direction="row"
                alignItems="flex-start"
                sx={{ width: "100%", gap: { xs: 1, sm: 1.5 } }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <PageHeader
                    title="Dados fitness"
                    description="Acompanhe e compartilhe dados do seu Google Health"
                    icon={MonitorHeart}
                  />
                </Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  sx={{
                    flexShrink: 0,
                    /* Alinha com a primeira linha do PageHeader (ícone + título). */
                    mt: { xs: 1, sm: 1.25 },
                  }}
                >
                  <Link
                    href="https://fluxmed.com.br/politica-de-privacidade/"
                    target="_blank"
                    rel="noreferrer"
                    underline="none"
                  >
                    <Chip
                      label="Política de privacidade"
                      size="small"
                      clickable
                      variant="outlined"
                      sx={{
                        fontWeight: 600,
                        color: "primary800.main",
                        borderColor: "primary300.main",
                        bgcolor: (theme) => alpha(theme.palette.primary100.main, 0.7),
                        "&:hover": {
                          bgcolor: (theme) => alpha(theme.palette.primary100.main, 1),
                          borderColor: "primary500.main",
                        },
                      }}
                    />
                  </Link>
                  <IconButton
                    onClick={onClose}
                    size="small"
                    aria-label="Fechar"
                    sx={{
                      color: "neutral600.main",
                      bgcolor: (t) => alpha(t.palette.neutral300.main, 0.3),
                      "&:hover": {
                        bgcolor: (t) => alpha(t.palette.neutral300.main, 0.45),
                      },
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>
            )}
          </DialogTitle>
          <DialogContent sx={{ px: { xs: 1, sm: 2 }, pb: 2 }}>
            {modalView === "share"
              ? sharePanel
              : modalView === "history"
                ? (
                  <>
                    {fitnessDateHeader(historyDateBusy)}
                    {historyPanel}
                  </>
                )
                : dataViewBody}
          </DialogContent>
          <DialogActions />
        </Dialog>
      )}
    </Fragment>
  );
}
